<script setup lang="ts">
/**
 * 我的会话。
 *
 * 三个分页守卫（cursor / hasMore / loading）放在**普通变量**里而不是 ref：
 * IntersectionObserver 会在滚动时连续触发，只有同步的普通变量才真的挡得住
 * 第二次请求——`ref` 的值在同一轮事件里还没更新，守卫形同虚设。
 *
 * 重命名是乐观更新：先改本地再发请求，失败回滚并提示。
 * 会话标题这种"改错了也无所谓"的字段，等一个 RTT 再显示新标题是更差的选择。
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  CONVERSATION_TITLE_MAX_LENGTH,
  ConversationPageSchema,
  DEFAULT_PAGE_LIMIT,
  ErrorCode,
  type Conversation,
  type ConversationPage,
} from '@spirit/contracts';

import AgeBandBadge from '../components/chat/AgeBandBadge.vue';
import DomainBadge from '../components/chat/DomainBadge.vue';
import Button from '../components/ui/Button.vue';
import Input from '../components/ui/Input.vue';
import Modal from '../components/ui/Modal.vue';
import SkeletonText from '../components/ui/SkeletonText.vue';
import { pushToast } from '../components/ui/toast-store';
import { apiRequest, isApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-store';
import { formatRelativeTime } from '../lib/format';
import { IgnoredDataSchema } from '../lib/schemas';

const PAGE_SIZE = DEFAULT_PAGE_LIMIT;

const auth = useAuth();

const items = ref<Conversation[]>([]);
const hasMore = ref(true);
const loading = ref(false);
const error = ref<string | null>(null);
const editing = ref<{ id: string; value: string } | null>(null);
const removing = ref<Conversation | null>(null);
const busyId = ref<string | null>(null);

let cursor: string | null = null;
let hasMoreGuard = true;
let loadingGuard = false;
const sentinelRef = ref<HTMLDivElement | null>(null);
let observer: IntersectionObserver | null = null;

async function loadMore(): Promise<void> {
  if (loadingGuard || !hasMoreGuard) return;
  loadingGuard = true;
  loading.value = true;
  error.value = null;
  try {
    const page = await apiRequest<ConversationPage>('/conversations', ConversationPageSchema, {
      method: 'GET',
      query: cursor === null ? { limit: PAGE_SIZE } : { limit: PAGE_SIZE, cursor },
    });
    cursor = page.nextCursor;
    hasMoreGuard = page.nextCursor !== null;
    hasMore.value = hasMoreGuard;
    // 按 id 去重：重复挂载或重复触发都不该出现两条一样的会话。
    const seen = new Set(items.value.map((item) => item.id));
    items.value = [...items.value, ...page.items.filter((item) => !seen.has(item.id))];
  } catch (caught) {
    error.value = isApiError(caught) ? caught.message : '会话列表加载失败，请稍后重试';
  } finally {
    loadingGuard = false;
    loading.value = false;
  }
}

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore();
    },
    { rootMargin: '200px' },
  );
  if (sentinelRef.value !== null) observer.observe(sentinelRef.value);
  if (auth.hydrated && auth.status === 'authenticated') void loadMore();
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

async function submitRename(target: Conversation, rawTitle: string): Promise<void> {
  const title = rawTitle.trim();
  if (title.length === 0 || title === target.title) {
    editing.value = null;
    return;
  }
  const previous = items.value;
  items.value = items.value.map((item) => (item.id === target.id ? { ...item, title } : item));
  editing.value = null;
  try {
    await apiRequest<null>(`/conversations/${target.id}`, IgnoredDataSchema, {
      method: 'PATCH',
      body: { title },
    });
    pushToast({ tone: 'success', title: '已重命名' });
  } catch (caught) {
    items.value = previous;
    pushToast({
      tone: 'danger',
      title: '重命名失败',
      description: isApiError(caught) ? caught.message : undefined,
    });
  }
}

async function confirmRemove(target: Conversation): Promise<void> {
  busyId.value = target.id;
  try {
    await apiRequest<null>(`/conversations/${target.id}`, IgnoredDataSchema, { method: 'DELETE' });
    items.value = items.value.filter((item) => item.id !== target.id);
    removing.value = null;
    pushToast({ tone: 'success', title: '会话已删除' });
  } catch (caught) {
    // 404 表示这条会话在服务端已经没了（另一个标签页删过、或重复点击）。
    // 这时把它继续留在列表里才是错的：用户会以为删除没生效，而刷新一下它又消失。
    if (isApiError(caught) && caught.code === ErrorCode.NOT_FOUND) {
      items.value = items.value.filter((item) => item.id !== target.id);
      removing.value = null;
      pushToast({ tone: 'info', title: '这条会话已不存在', description: '列表已同步' });
      return;
    }
    pushToast({
      tone: 'danger',
      title: '删除失败',
      description: isApiError(caught) ? caught.message : undefined,
    });
  } finally {
    busyId.value = null;
  }
}

const needsLogin = computed(() => auth.hydrated && auth.status !== 'authenticated');
const empty = computed(() => !loading.value && items.value.length === 0);

// 观察者只挂一次，sentinel 是 v-if 之外的常驻节点，这里不需要重新 observe。
watch(sentinelRef, (node) => {
  if (node !== null) observer?.observe(node);
});
</script>

<template>
  <div v-if="needsLogin" class="mx-auto w-full max-w-3xl px-4 py-12 text-center">
    <h1 class="text-lg font-semibold text-slate-900">登录后查看会话历史</h1>
    <p class="mt-2 text-sm text-slate-600">会话与额度都记在你的账号下。</p>
    <RouterLink to="/login" class="mt-6 inline-block"><Button>去登录</Button></RouterLink>
  </div>

  <div v-else class="mx-auto w-full max-w-3xl px-4 py-6">
    <div class="flex items-center justify-between gap-3">
      <h1 class="text-xl font-semibold text-slate-900">我的会话</h1>
      <RouterLink to="/chat"><Button size="sm">新对话</Button></RouterLink>
    </div>

    <p
      v-if="error !== null"
      class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      {{ error }}
      <button type="button" class="ml-2 underline underline-offset-2" @click="() => void loadMore()">重试</button>
    </p>

    <SkeletonText v-if="items.length === 0 && loading" :lines="4" class="mt-4" />

    <p v-else-if="empty" class="mt-4 text-sm text-slate-500">
      还没有会话。去
      <RouterLink to="/chat" class="text-brand-700 underline underline-offset-2">开始一次咨询</RouterLink>
      吧。
    </p>

    <ul v-else class="mt-4 space-y-2">
      <li
        v-for="item in items"
        :key="item.id"
        class="rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:border-brand-300"
      >
        <form
          v-if="editing !== null && editing.id === item.id"
          @submit.prevent="() => void submitRename(item, editing?.value ?? '')"
        >
          <Input
            label="会话标题"
            autofocus
            :model-value="editing.value"
            :maxlength="CONVERSATION_TITLE_MAX_LENGTH"
            :hint="`Enter 保存，Esc 取消（最多 ${CONVERSATION_TITLE_MAX_LENGTH} 字）`"
            @update:model-value="(value) => editing !== null && (editing.value = String(value))"
            @keydown.esc="editing = null"
          />
        </form>

        <div v-else class="flex items-start gap-3">
          <RouterLink :to="`/chat/${item.id}`" class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-slate-900">{{ item.title }}</span>
            <span class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <DomainBadge :domain="item.domain" />
              <AgeBandBadge v-if="item.ageBand !== null" :age-band="item.ageBand" />
              <span>{{ item.messageCount }} 条消息 · {{ formatRelativeTime(item.lastMessageAt) }}</span>
            </span>
          </RouterLink>
          <div class="flex shrink-0 items-center gap-1">
            <button
              type="button"
              class="rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
              @click="editing = { id: item.id, value: item.title }"
            >
              重命名
            </button>
            <button
              type="button"
              class="rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="busyId === item.id"
              @click="removing = item"
            >
              删除
            </button>
          </div>
        </div>
      </li>
    </ul>

    <!-- 无限滚动哨兵：无条件渲染，否则第一页之后就没有"再加载"的机会 -->
    <div ref="sentinelRef" aria-hidden="true" class="h-8" />
    <p v-if="loading && items.length > 0" class="text-center text-xs text-slate-400">加载中…</p>
    <p v-else-if="!hasMore && items.length > 0" class="text-center text-xs text-slate-400">没有更多会话了</p>

    <Modal
      :open="removing !== null"
      title="删除这个会话？"
      tone="danger"
      :description="`「${removing?.title ?? ''}」及其全部消息将被删除，且无法恢复。`"
      @close="removing = null"
    >
      <template #footer>
        <Button variant="secondary" @click="removing = null">取消</Button>
        <Button
          variant="danger"
          :disabled="busyId !== null"
          @click="() => removing !== null && void confirmRemove(removing)"
        >
          {{ busyId === null ? '删除' : '删除中…' }}
        </Button>
      </template>
    </Modal>
  </div>
</template>
